import { useContext, useEffect, useState } from "react";
import { PaymentContext, PaymentContextProvider, PaymentContextType } from "../../contexts/payment/PaymentContext";
import { ChannelModel } from "../../models/ChannelModel";
import { useLocation } from "react-router-dom";
import { DonationSingleCartResultInterface } from "../../contexts/payment/interfaces/donation-single-result.interface";
import { PaymentInterfaceParam } from "../channels/Channel";
import { Button, Card, Empty, message, Skeleton, Tabs } from "antd";
import axios from "axios";
import { CompanySingleResultInterface } from "../../models/CompanyModel";

function PaymentPlink() {
    return (
        <PaymentContextProvider>
            <PaymentPlinkState />
        </PaymentContextProvider>
    );
}

function PaymentPlinkState() {
    const [channel, setChannel] = useState<ChannelModel>();
    const [donationCartResult, setDonationCartResult] =
        useState<DonationSingleCartResultInterface>(undefined);
    const {
        initPayment,
        doPaymentPlink,
        paymentResultPlink,
        setPaymentResultPlink,
        getDonationCartDetailForStatus,
        donationCartDetailForStatus,
        setDonationCartResultV2,
    } = useContext(PaymentContext) as PaymentContextType;
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(true);

    // Load from localStorage if local state is missing (on refresh)
    useEffect(() => {
        if (!location.state) {
            const savedChannel = localStorage.getItem("plink_channel");
            const savedDonationResult = localStorage.getItem("plink_donation_result");
            const savedPaymentResult = localStorage.getItem("plink_payment_result");

            if (savedChannel) setChannel(JSON.parse(savedChannel));
            if (savedDonationResult) {
                const parsedResult = JSON.parse(savedDonationResult);
                setDonationCartResult(parsedResult);
                setDonationCartResultV2(parsedResult);
            }
            if (savedPaymentResult) {
                setPaymentResultPlink(JSON.parse(savedPaymentResult));
                setLoading(false);
            }
        }
    }, []);

    // Save to localStorage when things change
    useEffect(() => {
        if (channel) {
            localStorage.setItem("plink_channel", JSON.stringify(channel));
        }
    }, [channel]);

    useEffect(() => {
        if (donationCartResult) {
            localStorage.setItem("plink_donation_result", JSON.stringify(donationCartResult));
        }
    }, [donationCartResult]);

    useEffect(() => {
        if (paymentResultPlink && donationCartResult) {
            localStorage.setItem("plink_payment_result", JSON.stringify(paymentResultPlink));
            localStorage.setItem("plink_invoice_code", donationCartResult.data?.[0]?.invoice_code);
        }
    }, [paymentResultPlink, donationCartResult]);

    // Poll payment status every 5 seconds
    useEffect(() => {
        if (donationCartResult !== undefined) {
            const interval = setInterval(() => {
                getDonationCartDetailForStatus(
                    donationCartResult.data?.[0]?.invoice_code_parent as string
                );
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [donationCartResult]);

    useEffect(() => {
        if (location.state) {
            setChannel((location.state as PaymentInterfaceParam)?.item);
            setDonationCartResultV2(
                (location.state as PaymentInterfaceParam)?.donationCartResult
            );
            setDonationCartResult(
                (location.state as PaymentInterfaceParam)?.donationCartResult
            );
        }
    }, [location]);

    useEffect(() => {
        if (donationCartResult !== undefined && channel !== undefined) {
            const savedInvoiceCode = localStorage.getItem("plink_invoice_code");
            const currentInvoiceCode = donationCartResult.data?.[0]?.invoice_code;

            // Only do payment if we don't have a result yet or if it's for a different invoice
            if (!paymentResultPlink || savedInvoiceCode !== currentInvoiceCode) {
                for (const donation of donationCartResult.data) {
                    initPayment(donation?.id, channel?.id, "");
                }
                setLoading(true);
                doPaymentPlink(channel);
            } else {
                setLoading(false);
            }
        }
    }, [donationCartResult, channel]);

    useEffect(() => {
        if (paymentResultPlink) {
            setLoading(false);
        }
    }, [paymentResultPlink]);

    // Redirect based on payment status
    useEffect(() => {
        if (donationCartDetailForStatus !== undefined) {
            if (
                donationCartDetailForStatus?.data?.[0]?.status === "settlement" ||
                donationCartDetailForStatus?.data?.[0]?.status === "capture"
            ) {
                axios
                    .get(
                        `${process.env.REACT_APP_MAIN_URL}/public/companies/${process.env.REACT_APP_COMPANY_ID}`
                    )
                    .then((res) => {
                        const response = res.data as CompanySingleResultInterface;
                        const url = `${response?.data?.platform_donation}/payment/success?order_id=${donationCartDetailForStatus?.data?.[0]?.order_id}&result=success`;
                        window.location.href = url;
                    });
            } else if (
                donationCartDetailForStatus?.data?.[0]?.status === "cancel" ||
                donationCartDetailForStatus?.data?.[0]?.status === "deny"
            ) {
                axios
                    .get(
                        `${process.env.REACT_APP_MAIN_URL}/public/companies/${process.env.REACT_APP_COMPANY_ID}`
                    )
                    .then((res) => {
                        const response = res.data as CompanySingleResultInterface;
                        const url = `${response?.data?.platform_donation}/payment/failed?order_id=${donationCartDetailForStatus?.data?.[0]?.order_id}&result=failed`;
                        window.location.href = url;
                    });
            } else if (donationCartDetailForStatus?.data?.[0]?.status === "expire") {
                axios
                    .get(
                        `${process.env.REACT_APP_MAIN_URL}/public/companies/${process.env.REACT_APP_COMPANY_ID}`
                    )
                    .then((res) => {
                        const response = res.data as CompanySingleResultInterface;
                        const url = `${response?.data?.platform_donation}/payment/timeout?order_id=${donationCartDetailForStatus?.data?.[0]?.order_id}&result=expire`;
                        window.location.href = url;
                    });
            }
        }
    }, [donationCartDetailForStatus]);

    return (
        <div style={{ margin: 20 }}>
            <Skeleton loading={loading}>
                <h1>{channel?.title}</h1>
                <h2>{channel?.subtitle}</h2>

                {/* Primary CTA button — redirect to Plink payment page */}
                {paymentResultPlink?.data?.payment_page_url ? (
                    <Button
                        type="primary"
                        size={"large"}
                        style={{ marginBottom: 10, width: "100%" }}
                    >
                        <a
                            href={paymentResultPlink?.data?.payment_page_url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Bayar disini
                        </a>
                    </Button>
                ) : null}

                {paymentResultPlink?.status &&
                    paymentResultPlink?.data?.response_code === "PL000" ? (
                    <>
                        {/* Plink reference number */}
                        <Card style={{ marginBottom: 10 }}>
                            <div style={{ float: "left" }}>
                                <span className="account-number-text-block">
                                    Nomor Referensi Plink
                                </span>
                                <span className="account-number">
                                    {paymentResultPlink?.data?.plink_ref_no}
                                </span>
                                <br />
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        message.info(`Nomor Referensi berhasil di salin.`);
                                        navigator.clipboard.writeText(
                                            paymentResultPlink?.data?.plink_ref_no
                                        );
                                    }}
                                >
                                    Salin Nomor Referensi
                                </a>
                            </div>
                        </Card>
                    </>
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            paymentResultPlink?.data?.response_message ??
                            "Memproses pembayaran..."
                        }
                    />
                )}
            </Skeleton>

            {/* Plink payment link */}
            <Card hoverable title={"Plink"} style={{ marginBottom: 10 }}>
                <span className="transaction-detail-text">Link Pembayaran (Plink)</span>
                <span className="transaction-detail-description">
                    <a
                        href={paymentResultPlink?.data?.payment_page_url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Klik link ini untuk membayar melalui Plink
                    </a>
                </span>
            </Card>

            {/* How to pay */}
            {channel?.hows?.length > 0 ? (
                <Card hoverable title={"Cara Pembayaran"} style={{ marginBottom: 10 }}>
                    <Tabs>
                        {channel?.hows?.map((item, index) => (
                            <Tabs.TabPane tab={item.name} key={index}>
                                {item.steps?.map((step, i) => (
                                    <p key={i}>
                                        {i + 1}. {step}
                                    </p>
                                ))}
                            </Tabs.TabPane>
                        ))}
                    </Tabs>
                </Card>
            ) : (
                <></>
            )}
        </div>
    );
}

export default PaymentPlink;
